import {
  FundingAmount,
  FundingStatus,
  OrganizationApplicationParameters,
} from '@platform/client/schemas';
import { CheckboxList, FormsInputDropdown } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Section5.png';

type FormValues = {
  funding: FundingStatus[];
  fundingAmount?: FundingAmount;
};

const ORG_FUNDING_OPTIONS = [
  { text: '$10,000 - 50,000', textValue: FundingAmount['10,000_-_50,000'] },
  { text: '$50,000 - 100,000', textValue: FundingAmount['50,000_-_100,000'] },
  { text: '$100,000 - 250,000', textValue: FundingAmount['100,000_-_250,000'] },
  { text: '$250,000 - 500,000', textValue: FundingAmount['250,000_-_500,000'] },
  { text: '$500,000 - 1,000,000', textValue: FundingAmount['500,000_-_1,000,000'] },
  { text: '$1,000,000 - 2,500,000', textValue: FundingAmount['1,000,000_-_2,500,000'] },
  { text: '$2,500,000+', textValue: FundingAmount['2,500,000+'] },
];

const ORG_FUNDING_STATUS_OPTIONS = [
  {
    title: "We haven't raised any funding",
    value: FundingStatus['no-funding'],
  },
  {
    value: FundingStatus['grant-funding'],
    title: 'Grant funding',
  },
  {
    title: 'Angel or early-stage investment',
    value: FundingStatus['angel-funding'],
  },
  {
    value: FundingStatus['venture-capital-funding'],
    title: 'Venture capital (Series A or later)',
  },
];

export const OrgFundingSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<OrganizationApplicationParameters>(dataKey);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      funding: getApplicationDataByKey('funding'),
      fundingAmount: getApplicationDataByKey('fundingAmount'),
    },
  });
  // checkbox 'values' are the titles presented to user, value from form is database version
  const initialCheckboxSelection =
    ORG_FUNDING_STATUS_OPTIONS.filter((option) =>
      (getApplicationDataByKey('funding') || []).includes(option.value),
    )?.map((option) => option.title) || [];

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({
      funding: data.funding,
      fundingAmount: data.fundingAmount || null,
    });
    onContinue();
  };

  const onChangeFundingStatus = (value: string[]) => {
    const fundingStatus = value.map((v) => {
      const status = ORG_FUNDING_STATUS_OPTIONS.find((option) => option.title === v);
      return status ? status.value : v;
    }) as FundingStatus[];
    setValue('funding', fundingStatus);
  };

  const handleBack = () => {
    updateApplicationData({
      funding: watch('funding'),
      fundingAmount: watch('fundingAmount'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
      isEditProfile={props.isEditProfile}
    >
      <ApplicationPageContent
        title='Has your organization raised any funding in the past?'
        description={
          "It's okay if you haven't raised any funding yet - just let us know where you stand."
        }
      >
        <CheckboxList
          items={ORG_FUNDING_STATUS_OPTIONS.map((option) => option.title)}
          onChange={onChangeFundingStatus}
          error={errors.funding?.message}
          initiallySelected={initialCheckboxSelection}
        />
        {/** If the user has selected no funding, we don't ask how much they've raised */}
        {watch('funding') && !watch('funding').includes('no-funding') && (
          <FormsInputDropdown
            control={control}
            name='fundingAmount'
            rules={{ required: "Select how much funding you've raised to continue" }}
            label='How much funding have you raised, in USD?'
            placeholder='Select one'
            items={ORG_FUNDING_OPTIONS}
            type='default'
            error={errors.fundingAmount}
          />
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
